import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { isNotEmptyString } from '@racemap/utilities/functions/utils';
import { shortIdBuilder } from '@racemap/utilities/functions/utils';
import { type CSSProperties, type FC, useEffect, useMemo, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import { IconExclamation, IconFail, IconOk } from '../../Icon';

export interface Props {
  value?: boolean;
  label?: string;
  disabled?: boolean;
  onChange?: (newValue: boolean) => void;
  size?: 'sm' | 'lg';
  variant?: 'success' | 'danger' | 'warning';
  title?: string;
  className?: string;
  style?: CSSProperties;
  labelFirst?: boolean;
}

export const Switch: FC<Props> = ({
  value,
  onChange,
  label,
  disabled = false,
  size = 'sm',
  variant,
  title,
  style,
  className,
  labelFirst = false,
}) => {
  const [cacheValue, setCacheValue] = useState(value);
  const id = useMemo(() => shortIdBuilder(), []);
  const labelText = isNotEmptyString(label) ? label : cacheValue ? 'ON' : 'OFF';
  const variantIcon = getVariantIcon(variant);
  const variantColor = getVariantColor(variant);
  const iconWrapperStyle = useSpring({
    to: {
      opacity: cacheValue ? 1 : 0,
    },
    delay: 250,
  });

  useEffect(() => {
    if (cacheValue !== value) {
      setCacheValue(value);
    }
  }, [value]);

  const handleSwitchChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    setCacheValue(target.checked);
    onChange?.(target.checked);
  };
  const SwitchLabel: FC<{ style?: CSSProperties }> = ({ style }) => (
    <Text size={size} color={cacheValue ? variantColor : undefined} style={style}>
      {labelText}
    </Text>
  );

  return (
    <Wrapper
      disabled={disabled}
      title={title}
      className={className}
      style={style}
      labelFirst={labelFirst}
    >
      <Input
        type="checkbox"
        id={id}
        checked={cacheValue}
        onChange={handleSwitchChange}
        color={variantColor}
      />
      <LabelAndIcon>
        <Label htmlFor={id} disabled={disabled} size={size} />
        <IconWrapper size={size} style={iconWrapperStyle}>
          {cacheValue && variantIcon}
        </IconWrapper>
      </LabelAndIcon>
      <SwitchLabel />
    </Wrapper>
  );
};

const getVariantIcon = (variant?: 'success' | 'danger' | 'warning'): React.ReactElement => {
  const color = getVariantColor(variant) || RacemapColors.PaleBlue;

  if (variant == null) return <></>;
  switch (variant) {
    case 'danger':
      return <IconFail color={color} />;
    case 'warning':
      return <IconExclamation color={color} />;
    case 'success':
      return <IconOk color={color} />;
  }
};

const getVariantColor = (variant?: 'success' | 'danger' | 'warning'): string | undefined => {
  if (variant == null) return undefined;
  switch (variant) {
    case 'danger':
      return RacemapColors.DarkRed;
    case 'warning':
      return RacemapColors.DarkYellow;
    case 'success':
      return RacemapColors.BaseGreen;
  }
};

const Wrapper = styled.div<{ disabled: boolean; labelFirst: boolean }>`
  display: flex;
  flex-direction: ${({ labelFirst }) => (labelFirst ? 'row-reverse' : 'row')};
  align-items: center;
  flex-wrap: nowrap;
  position: relative;

  ${({ disabled }) =>
    disabled
      ? `
    opacity: 0.5;
    cursor: not-allowed;
  `
      : ''}
`;

const LabelAndIcon = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const IconWrapper = styled(animated.div)<{ size: 'sm' | 'lg' }>`
  display: flex;
  align-items: center;
  width: ${({ size }) => (size === 'sm' ? '8px' : '11px')};
  min-width: ${({ size }) => (size === 'sm' ? '8px' : '11px')};
  height: ${({ size }) => (size === 'sm' ? '8px' : '11px')};
  position: absolute;
  right: ${({ size }) => (size === 'sm' ? '3px' : '4px')};
  pointer-events: none;

  svg {
    width: ${({ size }) => (size === 'sm' ? '8px' : '11px')};
    height: ${({ size }) => (size === 'sm' ? '8px' : '11px')};
    pointer-events: none;
  }
`;

const Text = styled.div<{ size: 'sm' | 'lg'; color?: string }>`
  margin: ${({ size }) => (size === 'sm' ? '0 3px' : '0 5px')};
  font-weight: ${({ size }) => (size === 'sm' ? '400' : '500')};
  font-size: ${({ size }) => (size === 'sm' ? '14px' : '16px')};
  color: ${({ color }) => color || RacemapColors.DarkGray};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Input = styled.input<{ color?: string }>`
  position: absolute !important;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  bottom: 0;

  :checked + div > label {
    background: ${({ color = RacemapColors.PaleBlue }) => color};
  }

  :checked + div > label:after {
    right: 1px;
    background-color: #fff;
    box-shadow: -2px 0px 0px rgba(0, 0, 0, 0.1);
  }
`;

const Label = styled.label<{ disabled: boolean; size: 'sm' | 'lg' }>`
  display: inline-block;
  position: relative;
  height: ${({ size }) => (size === 'sm' ? '14px' : '20px')};
  width: ${({ size }) => (size === 'sm' ? '24px' : '32px')};
  min-width: ${({ size }) => (size === 'sm' ? '24px' : '32px')};
  margin-bottom: unset;
  background-color: #e9ecef;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  font-size: 0;
  color: transparent;
  border-radius: 50vmin;
  border: solid 1px #aeb5bd;
  transition: background-color 500ms ease;

  &:after {
    transition: right 500ms ease, background-color 500ms ease, box-shadow 500ms ease;
  }

  :after {
    content: '';
    display: block;
    height: 86%;
    aspect-ratio: 1;
    position: absolute;
    top: 6.5%;
    right: ${({ size }) => (size === 'sm' ? '47%' : '44%')};
    border-radius: 50%;
    background-color: #aeb5bd;
  }
`;
